import React, { FC } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import classNames from 'classnames';
import { List } from 'immutable';
import { compose } from '@reduxjs/toolkit';

import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { useTranslation } from 'app/lang';
// --
import { MatchType, SlotType } from 'app/lib/betting/betting-entities';
import { getTruncatedNumber } from 'app/lib/formatters';
import { MatchCenterLink, MatchDetails, Stake, StakeBlank } from 'app/modules/Betting';

import { GRID_COLUMNS_COUNT } from '../../constants';
import { onlyLiveMatches } from '../../selectors';
import { matches } from '../../selectors/entities';

import dictionary from './Match.dictionary.json';

import styles from '../../InterestingMatches.module.scss';


// --

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BettingContextProps = any;

type StoreProps = {
  stakeIds?: List<number | null>;
  betCount?: number;
  slotType?: number;
  showLiveMatches?: boolean;
  isOutrightMatch?: boolean;
};

type InterestingMatchesMatchProps = {
  matchId: number;
};

const InterestingMatchesMatch: FC<StoreProps & InterestingMatchesMatchProps> = ({
  matchId,
  stakeIds,
  betCount,
  slotType,
  showLiveMatches,
  isOutrightMatch,
}) => {
  const { t } = useTranslation();

  if (!slotType) {
    return null;
  }

  if (showLiveMatches && slotType === SlotType.PREMATCH) {
    return null;
  }

  const notVisibleStake = stakeIds?.every((stakeId) => !stakeId);
  const hasStakeIdsSize = stakeIds?.size
    && GRID_COLUMNS_COUNT.includes(stakeIds.size);

  const NotVisibleStakes = () => (
    <MatchCenterLink matchId={matchId}
                     className={classNames(styles.contentWithinBets, {
                       [styles.threeMainLineStake]: hasStakeIdsSize,
                     })}>
      <span>{t(dictionary.linkText)}</span>
      ({betCount && `+${getTruncatedNumber(betCount, { accuracy: { thousands: 1 } })}`})
    </MatchCenterLink>
  );

  return (
    <div data-test={`interestingMatches-match-row-${matchId}`}
         className={styles.matchRow}>
      <div className={styles.matchDetails}>
        <MatchDetails matchId={matchId} />
      </div>

      {notVisibleStake
        ? (
          <NotVisibleStakes />
        ) : (
          <div className={classNames(styles.stakesContainer, {
            [styles.threeMainLineStake]: hasStakeIdsSize,
          })}>
            {stakeIds && stakeIds.map((stakeId, index) => {
              const key = stakeId ? `${stakeId}-${index}` : `${matchId}-${index}/blank`;

              if (isOutrightMatch) {
                return;
              }

              if (!stakeId) {
                return (
                  <StakeBlank key={key}
                              className={styles.mainLineStakes} />
                );
              }

              return (
                <Stake
                    key={key}
                    matchId={matchId}
                    stakeId={stakeId}
                    className={styles.mainLineStakes} />
              );
            })}
          </div>
        )}

      <MatchCenterLink matchId={matchId}
                       className={styles.betsCount}>
        {betCount && `+${getTruncatedNumber(betCount, { accuracy: { thousands: 1 } })}`}
      </MatchCenterLink>
    </div>
  );
};

const createMapStateToProps: MapStateToProps<
  StoreProps,
  BettingContextProps & InterestingMatchesMatchProps,
  ReduxState
> = (
  state,
  {
    $betting: {
      namespace,
      grid: {
        selectors: {
          mainLineId,
          visibleLineStakeIdsSelector,
        },
      },
    },
    matchId,
  },
) => {
  const match = matches(state).get(matchId);

  if (!match) {
    console.error(`Match in Namespace: ${namespace} and with MatchId: ${matchId} is undefined in redux state.
                   path: src/modules/LandingPage/InterestingMatches/components/Match/index.tsx`);
    return {};
  }

  const lineId = mainLineId(state, { matchId });

  return {
    stakeIds: lineId === null
      ? null
      : visibleLineStakeIdsSelector(state, lineId, matchId),
    betCount: match.betsCount || 0,
    slotType: match.slotType,
    showLiveMatches: onlyLiveMatches(state),
    isOutrightMatch: match.type === MatchType.OUTRIGHT,
  };
};

export default compose<FC<InterestingMatchesMatchProps>>(
  withinBettingContext,
  connect(createMapStateToProps),
)(InterestingMatchesMatch);
